// import moment from 'moment';
import { put } from 'redux-saga/effects';
import { toggleLoadingModal } from '../redux/action/index';
import {
  TYPE_IDENTITY,
  TYPE_ADDRESS,
  TYPE_SELFIE,
  SUBTYPE_PASSPORT,
  SUBTYPE_ID_CARD,
  SUBTYPE_DRIVER_LIC,
  SUBTYPE_BANK_STATEMENT,
  SUBTYPE_GAS_BILL,
  SUBTYPE_SELFIE,
  SUBTYPE_SELFIE_WITH_ID,
  SUBTYPE_WATER_BILL,
  SUBTYPE_ELECTRICITY_BILL,
  SUBTYPE_INTERNET_OR_TV_BILL,
  SUBTYPE_FIXED_SERVICES_BILL
} from '../utils/constants';

/**
 * Normalizes data for the API
 *
 * @param {object} data data from form
 * @returns {object} normalized object for AUTH API
 * @author jozef.repan
 */
export const createUserProfileApiObject = data => {
  const result = {
    title: null,
    username: data.userName ? data.userName.toString().trim() : null,
    first_name: data.firstName ? data.firstName.toString().trim() : null,
    last_name: data.lastName ? data.lastName.toString().trim() : null,
    middle_name: null,
    nationality: data.nationality ? data.nationality : null,
    // email: data.email.trim(),
    state: null,
    // phone_number: data.phonePrefix.value + data.phoneNumber.trim(),
    country: data.country ? data.country : null,
    city: data.city ? data.city.trim() : null,
    address: data.streetAndNumber
      ? data.streetAndNumber.toString().trim()
      : null,
    post_code: data.zip ? data.zip.toString().trim() : null,
    // eslint-disable-next-line no-use-before-define
    date_of_birth: createBirthdayString(
      data.dayOfBirthday,
      data.monthOfBirthday,
      data.yearOfBirthday
    ),
    refs: data.refs,
    pep: data.pep
  };
  return result;
};

/**
 * Normalizes data for the API
 *
 * @param {string} email
 * @returns {object} normalized object for AUTH API
 * @author jozef.repan
 */
export const createChangeEmailApiObject = email => ({ email: email.trim() });

/**
 * Normalizes Phone number and auth code for verified phone change
 * @param {string} phoneNumber
 * @param {string} code
 */
export const createChangePhoneVerifiedApiObject = (phoneNumber, code) => ({
  phone: phoneNumber.trim().replace(' ', ''),
  code: code.trim()
});

/**
 * Normalizes data for the API
 *
 * @param {string} phoneNumebr
 * @returns {object} normalized object for AUTH API
 * @author jozef.repan
 */
export const createChangePhoneApiObject = phoneNumber => ({
  phone_number: phoneNumber.trim()
});

/**
 * Normalizes data for the API
 *
 * @param {string} email
 * @param {string} code
 * @returns {object} normalized object for AUTH API
 * @author jozef.repan
 */
export const createVerifyEmailApiObject = (email, code) => ({
  email: email.trim(),
  code: code.trim()
});

/**
 * Normalizes data for the API
 *
 * @param {object} data
 * @returns {object} normalized object for AUTH API
 * @author jozef.repan
 */
export const createUserPasswordApiObject = data => {
  const result = {
    old_password: data.currentPassword,
    password: data.newPassword,
    password_confirmation: data.confirmPassword
  };
  return result;
};

/**
 * Normalizes data for the API
 *
 * @param {string} day - DD format
 * @param {string} month  - MM format
 * @param {string} year - YYYY format
 * @author jozef.repan
 */
export const createBirthdayString = (day, month, year) => {
  // if one of this is null, everything is null
  if (
    day === null ||
    day === '' ||
    month === null ||
    month === '' ||
    year === null ||
    year === ''
  ) {
    return null;
  }
  return `${year}-${month}-${day}`;
};

/**
 * Normalizes data for the API
 *
 * @param {object} data
 * @returns {object} - normalized object
 * @author jozef.repan
 */
export const prepareKycDocumentFormData = data => {
  const formData = new FormData();

  // create expiration
  const expiration = data.withoutExpiration
    ? null
    : `${data.yearOfExpiration}-${data.monthOfExpiration}-${data.dayOfExpiration}`;

  switch (data.typeOfDocument) {
    case SUBTYPE_ID_CARD:
      formData.append('type', TYPE_IDENTITY);
      formData.append('subtype', data.typeOfDocumentId);
      formData.append('frontSide', data.idDocument[0]);
      formData.append('backSide', data.idDocument[1]);
      formData.append('expiration', expiration);
      formData.append('documentNumber', data.idNumber);
      break;
    case SUBTYPE_PASSPORT:
      formData.append('type', TYPE_IDENTITY);
      formData.append('subtype', data.typeOfDocumentId);
      formData.append('frontSide', data.idDocument[0]);
      formData.append('backSide', null);
      formData.append('expiration', expiration);
      formData.append('documentNumber', data.idNumber);
      break;
    case SUBTYPE_DRIVER_LIC:
      formData.append('type', TYPE_IDENTITY);
      formData.append('subtype', data.typeOfDocumentId);
      formData.append('frontSide', data.idDocument[0]);
      formData.append('backSide', data.idDocument[1]);
      formData.append('expiration', expiration);
      formData.append('documentNumber', data.idNumber);
      break;
    case SUBTYPE_BANK_STATEMENT:
      formData.append('type', TYPE_ADDRESS);
      formData.append('subtype', data.typeOfDocumentId);
      formData.append('frontSide', data.proofOfAddress[0]);
      formData.append('backSide', data.proofOfAddress[1]);
      break;
    case SUBTYPE_GAS_BILL:
      formData.append('type', TYPE_ADDRESS);
      formData.append('subtype', data.typeOfDocumentId);
      formData.append('frontSide', data.proofOfAddress[0]);
      formData.append('backSide', data.proofOfAddress[1]);
      break;
    case SUBTYPE_FIXED_SERVICES_BILL:
      formData.append('type', TYPE_ADDRESS);
      formData.append('subtype', data.typeOfDocumentId);
      formData.append('frontSide', data.proofOfAddress[0]);
      formData.append('backSide', null);
      break;
    case SUBTYPE_INTERNET_OR_TV_BILL:
      formData.append('type', TYPE_ADDRESS);
      formData.append('subtype', data.typeOfDocumentId);
      formData.append('frontSide', data.proofOfAddress[0]);
      formData.append('backSide', null);
      break;
    case SUBTYPE_WATER_BILL:
      formData.append('type', TYPE_ADDRESS);
      formData.append('subtype', data.typeOfDocumentId);
      formData.append('frontSide', data.proofOfAddress[0]);
      formData.append('backSide', null);
      break;
    case SUBTYPE_ELECTRICITY_BILL:
      formData.append('type', TYPE_ADDRESS);
      formData.append('subtype', data.typeOfDocumentId);
      formData.append('frontSide', data.proofOfAddress[0]);
      formData.append('backSide', null);
      break;
    case SUBTYPE_SELFIE:
      formData.append('type', TYPE_SELFIE);
      formData.append('subtype', data.typeOfDocumentId);
      formData.append('frontSide', data.selfie[0]);
      formData.append('backSide', null);
      break;
    case SUBTYPE_SELFIE_WITH_ID:
      formData.append('type', TYPE_SELFIE);
      formData.append('subtype', data.typeOfDocumentId);
      formData.append('frontSide', data.selfie[0]);
      formData.append('backSide', null);
      break;
    default:
      break;
  }

  return formData;
};

/**
 * Normalizes data for the API
 *
 * @param type
 * @param value
 * @author jozef.repan
 */
export const create2FaApiObject = (type, value) => {
  const response = {};

  switch (type) {
    case 'email':
      response.email_verification = value;
      break;
    case 'sms':
      response.phone_number_verification = value;
      break;
    case 'totp':
      response.TOTP = value;
      break;
    case 'u2f':
      response.U2F = value;
      break;
    default:
      break;
  }
  return response;
};

/**
 * Saga decorator to show loading indicator while the function is being performed
 * @param {*} saga saga function generator
 * @author Boris Naništa
 */
export const withLoadingIndicator = saga =>
  function* inner(action) {
    yield put(toggleLoadingModal(true));
    yield* saga(action);
    yield put(toggleLoadingModal(false));
  };
